import { useState, useEffect, useMemo } from 'react';
import {
  Plus, Edit3, History, X, Trash2, Eye, EyeOff,
  Package, IndianRupee, ShoppingCart, Loader2, AlertTriangle,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

// ── Types ─────────────────────────────────────────────────────────────────────

type Visibility = 'all' | 'restricted';

interface ScrapItem {
  id: string;
  name: string;
  unit: string;
  stock: number;
  price: number;
  maxDiscount: number;
  visibility: Visibility;
  value: number;
}

interface SaleRecord {
  date: string;
  qty: number;
  price: number;
  buyer: string;
  discount: number;
}

const UNIT_OPTIONS = ['kg', 'pcs', 'meters', 'liters', 'sheets', 'rolls', 'sets'];

// ── Component ─────────────────────────────────────────────────────────────────

export default function ScrapManagementPage() {
  const [items, setItems] = useState<ScrapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<ScrapItem | null>(null);
  const [showHistory, setShowHistory] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formUnit, setFormUnit] = useState('kg');
  const [formPrice, setFormPrice] = useState(0);
  const [formMaxDiscount, setFormMaxDiscount] = useState(10);
  const [formVisibility, setFormVisibility] = useState<Visibility>('restricted');

  // ── Fetch scrap products ────────────────────────────────────────────────

  async function fetchScrapProducts() {
    try {
      const res = await api.get('/products', { params: { productType: 'Scrap' } });
      const products = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

      const mapped: ScrapItem[] = products.map((p: any) => ({
        id: p.id,
        name: p.name ?? p.productName ?? '',
        unit: p.unit ?? p.uom ?? 'pcs',
        stock: p.stockQty ?? p.stock ?? 0,
        price: p.sellingPrice ?? p.price ?? 0,
        maxDiscount: p.maxDiscount ?? 10,
        visibility: p.visibility ?? 'restricted',
        value: (p.stockQty ?? p.stock ?? 0) * (p.sellingPrice ?? p.price ?? 0),
      }));

      // Fetch inventory for each product to get accurate stock quantities
      const withInventory = await Promise.all(
        mapped.map(async (item) => {
          try {
            const invRes = await api.get('/inventory', { params: { productId: item.id } });
            const invData = Array.isArray(invRes.data) ? invRes.data : (invRes.data?.data ?? []);
            const totalStock = invData.reduce((sum: number, inv: any) => sum + (inv.quantity ?? inv.stock ?? 0), 0);
            if (totalStock > 0) {
              return { ...item, stock: totalStock, value: totalStock * item.price };
            }
          } catch {
            // Inventory endpoint may not exist or return empty — use product stock
          }
          return item;
        })
      );

      setItems(withInventory);
    } catch (err) {
      console.error('Failed to fetch scrap products:', err);
      setItems([]);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchScrapProducts();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Stats
  const stats = useMemo(() => {
    const totalValue = items.reduce((s, i) => s + i.value, 0);
    const totalItems = items.reduce((s, i) => s + i.stock, 0);
    return { totalValue, totalItems, soldThisMonth: 0 };
  }, [items]);

  function openAdd() {
    setEditItem(null);
    setFormName('');
    setFormUnit('kg');
    setFormPrice(0);
    setFormMaxDiscount(10);
    setFormVisibility('restricted');
    setShowModal(true);
  }

  function openEdit(item: ScrapItem) {
    setEditItem(item);
    setFormName(item.name);
    setFormUnit(item.unit);
    setFormPrice(item.price);
    setFormMaxDiscount(item.maxDiscount);
    setFormVisibility(item.visibility);
    setShowModal(true);
  }

  async function handleSave() {
    if (!formName.trim()) return;
    setSaving(true);

    try {
      if (editItem) {
        // Update existing product
        await api.put(`/products/${editItem.id}`, {
          name: formName,
          unit: formUnit,
          sellingPrice: formPrice,
          maxDiscount: formMaxDiscount,
          visibility: formVisibility,
          productType: 'Scrap',
        });
      } else {
        // Create new scrap product
        await api.post('/products', {
          name: formName,
          unit: formUnit,
          sellingPrice: formPrice,
          maxDiscount: formMaxDiscount,
          visibility: formVisibility,
          productType: 'Scrap',
        });
      }
      setShowModal(false);
      await fetchScrapProducts();
    } catch (err) {
      console.error('Failed to save scrap product:', err);
      // Fallback: update local state
      if (editItem) {
        setItems((prev) =>
          prev.map((i) =>
            i.id === editItem.id
              ? { ...i, name: formName, unit: formUnit, price: formPrice, maxDiscount: formMaxDiscount, visibility: formVisibility, value: i.stock * formPrice }
              : i
          )
        );
      } else {
        const newItem: ScrapItem = {
          id: String(Date.now()),
          name: formName,
          unit: formUnit,
          stock: 0,
          price: formPrice,
          maxDiscount: formMaxDiscount,
          visibility: formVisibility,
          value: 0,
        };
        setItems((prev) => [...prev, newItem]);
      }
      setShowModal(false);
    } finally {
      setSaving(false);
    }
  }

  const historyItem = showHistory ? items.find((i) => i.id === showHistory) : null;

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-sm text-slate-500">Loading scrap products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Scrap & Wastage</h1>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Wastage Product
        </button>
      </div>

      {/* ── Stats Row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: 'Total Stock Value', value: formatINR(stats.totalValue), cls: 'text-blue-600', bg: 'bg-blue-50', Icon: IndianRupee },
          { label: 'Items in Stock', value: stats.totalItems.toLocaleString('en-IN'), cls: 'text-green-600', bg: 'bg-green-50', Icon: Package },
          { label: 'Sold This Month', value: formatINR(stats.soldThisMonth), cls: 'text-amber-600', bg: 'bg-amber-50', Icon: ShoppingCart },
        ].map(({ label, value, cls, bg, Icon }) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg ${bg} p-2.5`}>
                <Icon className={`h-5 w-5 ${cls}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-xl font-bold ${cls}`}>{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Empty State ───────────────────────────────────────────────────── */}
      {items.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-16 text-center">
          <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">No scrap products yet</h2>
          <p className="text-sm text-gray-500 mb-6">Add your first scrap product to start tracking wastage and scrap inventory.</p>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add your first scrap product
          </button>
        </div>
      ) : (
        /* ── Table ──────────────────────────────────────────────────────────── */
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Unit</th>
                  <th className="px-6 py-3 text-right">Current Stock</th>
                  <th className="px-6 py-3 text-right">Selling Price</th>
                  <th className="px-6 py-3 text-right">Max Discount %</th>
                  <th className="px-6 py-3 text-right">Stock Value</th>
                  <th className="px-6 py-3 text-center">Visibility</th>
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-3 font-medium text-slate-900">{item.name}</td>
                    <td className="px-6 py-3 text-gray-500">{item.unit}</td>
                    <td className="px-6 py-3 text-right text-gray-700 font-medium">
                      {item.stock.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-700">{formatINR(item.price)}/{item.unit}</td>
                    <td className="px-6 py-3 text-right text-gray-500">{item.maxDiscount}%</td>
                    <td className="px-6 py-3 text-right font-semibold text-slate-900">{formatINR(item.value)}</td>
                    <td className="px-6 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          item.visibility === 'all'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {item.visibility === 'all' ? (
                          <><Eye className="h-3 w-3" /> All</>
                        ) : (
                          <><EyeOff className="h-3 w-3" /> Restricted</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowHistory(item.id)}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-green-600 transition-colors"
                          title="View Sales History"
                        >
                          <History className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">
                {editItem ? 'Edit Wastage Product' : 'Add Wastage Product'}
              </h2>
              <button onClick={() => setShowModal(false)} className="rounded-lg p-1 hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., SS Sheet Offcuts"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Unit</label>
                <select
                  value={formUnit}
                  onChange={(e) => setFormUnit(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Selling Price (per {formUnit})</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">&#8377;</span>
                    <input
                      type="number"
                      min={0}
                      value={formPrice}
                      onChange={(e) => setFormPrice(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full rounded-lg border border-gray-300 py-2 pl-7 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Max Discount %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={formMaxDiscount}
                    onChange={(e) => setFormMaxDiscount(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Visibility</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormVisibility('all')}
                    className={`flex-1 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                      formVisibility === 'all'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <Eye className="mx-auto mb-1 h-4 w-4" />
                    Visible to All
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormVisibility('restricted')}
                    className={`flex-1 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                      formVisibility === 'restricted'
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <EyeOff className="mx-auto mb-1 h-4 w-4" />
                    Restricted
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {formVisibility === 'restricted'
                    ? 'Only Sales Head + Admin can view and sell this item'
                    : 'All team members can view this item'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formName.trim() || saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editItem ? 'Save Changes' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sales History Modal ────────────────────────────────────────────── */}
      {showHistory && historyItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Sales History</h2>
                <p className="text-sm text-gray-500">{historyItem.name}</p>
              </div>
              <button onClick={() => setShowHistory(null)} className="rounded-lg p-1 hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="py-12 text-center text-gray-400">
              <History className="mx-auto h-10 w-10 mb-2" />
              <p className="text-sm">No sales history for this item.</p>
              <p className="text-xs text-gray-400 mt-1">Sales tracking integration coming soon.</p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowHistory(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { ScrapManagementPage };
